import { useAuth0 } from '@auth0/auth0-react'
import { useCallback, useSyncExternalStore } from 'react'
import { useAddUserMetadata } from '~/api/app/user-metadata.api'
import { useAuthenticatedUser } from '~/auth/user.hooks'

/**
 * Whether the mapping shows only the ad units the media's stacks deploy.
 *
 * Stored on the user in Auth0 `user_metadata`, so the choice follows them to another browser rather
 * than sitting beside `menuOpenGroups` in one machine's localStorage.
 *
 * A hook rather than state on the mapping view because two screens read it and they are siblings, not
 * parent and child: the spreadsheet filters its rows on it, and the "Mapping options" flyout — opened
 * from the seat page header, which knows nothing of the filter — warns on it before handing over a CSV.
 * Both are mounted at once while the flyout is open, so a `useState` in each would let them disagree
 * about what the file is going to contain.
 *
 * Hence the module-level choice below. It is not a cache of the stored value: Auth0 hands the new
 * metadata back only when the token is renewed, so between the write and the renewal the token still
 * claims the old value, and the snapshot is the only place the new one exists.
 */

/** Undefined until something is toggled this session, so the stored value answers first. */
let pendingChoice: boolean | undefined
const listeners = new Set<() => void>()
const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
const getPendingChoice = () => pendingChoice
const publish = (choice: boolean | undefined) => {
  pendingChoice = choice
  listeners.forEach((listener) => listener())
}

export const useStackedAdUnitsOnly = (): [boolean, (value: boolean) => void] => {
  const user = useAuthenticatedUser()
  const { getAccessTokenSilently } = useAuth0()
  const { mutateAsync: addUserMetadata } = useAddUserMetadata({ errorToast: 'Error while saving your mapping preference' })

  const pending = useSyncExternalStore(subscribe, getPendingChoice)
  const stackedAdUnitsOnly = pending ?? user.preferences?.mappingStackedAdUnitsOnly ?? false

  const setStackedAdUnitsOnly = useCallback(
    async (value: boolean) => {
      const previousChoice = pendingChoice
      publish(value)

      try {
        await addUserMetadata({
          scopeId: user.currentScopeId,
          // Both are sent back as they stand rather than left out: they sit under `user_metadata`'s root,
          // which Auth0 replaces wholesale, so omitting one would clear it.
          alertEmail: user.alertEmail ?? {},
          preferences: { ...user.preferences, mappingStackedAdUnitsOnly: value },
        })
        // The same renewal the profile page does after writing metadata. Without it the claim keeps the
        // old value, and reloading the page would put the filter back where it was.
        await getAccessTokenSilently({ cacheMode: 'off' })
      } catch {
        // The switch moved before the write was known to have worked, so put it back where it was.
        // Left alone, the toast would say the save failed while the control went on claiming it worked,
        // and the mapping would filter on a preference the user does not have. The mutation raises its
        // own toast; this is only here to undo the optimistic move, and swallowing it keeps the
        // un-awaited `onChange` caller from an unhandled rejection.
        publish(previousChoice)
      }
    },
    [addUserMetadata, getAccessTokenSilently, user.currentScopeId, user.alertEmail, user.preferences]
  )

  return [stackedAdUnitsOnly, setStackedAdUnitsOnly]
}
